import { Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFoundingMemberProgress } from '@/hooks/useFoundingMemberProgress';
import type { FoundingMemberPath } from '@/types/founding-member';

interface FoundingMemberBadgeProps {
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
  variant?: 'badge' | 'inline' | 'pill';
  className?: string;
  forceShow?: boolean;
}

const sizeClasses = {
  sm: 'text-[10px] px-2 py-0.5',
  md: 'text-xs px-3 py-1',
  lg: 'text-sm px-4 py-1.5',
};

const iconSizes = {
  sm: 'w-3 h-3',
  md: 'w-3.5 h-3.5',
  lg: 'w-4 h-4',
};

const shimmerClasses = [
  'relative overflow-hidden',
  'after:absolute after:inset-0',
  'after:bg-gradient-to-r after:from-transparent after:via-white/10 after:to-transparent',
  'after:-translate-x-full',
  'hover:after:translate-x-full',
  'after:transition-transform after:duration-700',
].join(' ');

function getTooltipText(path: FoundingMemberPath | null): string {
  switch (path) {
    case 'streak_30':
      return 'Founding Member — 30-day habit streak';
    case 'goals_3':
      return 'Founding Member — 3 goals completed';
    case 'feedback_10':
      return 'Founding Member — 10 feedback submissions';
    case 'referrals_5':
      return 'Founding Member — 5 referrals';
    default:
      return 'Founding Member';
  }
}

export function FoundingMemberBadge({
  size = 'md',
  showTooltip = true,
  variant = 'badge',
  className,
  forceShow = false,
}: FoundingMemberBadgeProps) {
  const { isQualified, qualificationPath, isLoading } = useFoundingMemberProgress();

  if (isLoading) return null;
  if (!isQualified && !forceShow) return null;

  const icon = <Trophy className={cn(iconSizes[size], 'shrink-0')} />;

  let badge: React.ReactElement;

  if (variant === 'inline') {
    badge = (
      <span
        className={cn('inline-flex text-purple-400', className)}
        data-testid="founding-member-badge"
      >
        {icon}
      </span>
    );
  } else if (variant === 'pill') {
    badge = (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full font-semibold',
          'bg-gradient-to-r from-purple-500 to-teal-500 text-white',
          sizeClasses[size],
          shimmerClasses,
          className,
        )}
        data-testid="founding-member-badge"
      >
        {icon}
        <span>Founding Member</span>
      </span>
    );
  } else {
    badge = (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-md font-semibold',
          'border border-purple-500/30 bg-purple-500/10 text-purple-300',
          sizeClasses[size],
          shimmerClasses,
          className,
        )}
        data-testid="founding-member-badge"
      >
        {icon}
        <span>Founding Member</span>
      </span>
    );
  }

  if (!showTooltip) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          <p>{getTooltipText(qualificationPath)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
